import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Send, Truck, RefreshCw, Ruler, Droplets, Sparkles } from "lucide-react";

const InfoBlock = ({ id, icon: Icon, title, children, testid }) => (
  <section
    id={id}
    data-testid={testid}
    className="border-2 border-zinc-800 bg-zinc-900/40 p-6 md:p-8 scroll-mt-24"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 border-2 border-zinc-300/60 text-zinc-200 shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="font-display text-2xl sm:text-3xl text-zinc-50 uppercase tracking-widest leading-none">
        {title}
      </h2>
    </div>
    <div className="text-zinc-300 text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const ContactPage = () => {
  const location = useLocation();
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: "",
    inquiry_type: "general",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Fill all required fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent");
      setForm({
        name: "",
        email: "",
        subject: "General Inquiry",
        message: "",
        inquiry_type: "general",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setLoading(false);
    }
  };

  // Scroll to the requested section on load (?#shipping / #returns / etc.)
  useEffect(() => {
    if (!location.hash) return;
    const el = document.querySelector(location.hash);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-zinc-950">
      <NavBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-10">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-8 h-[1px] bg-zinc-500" />
            <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-200 uppercase">
              [ 01 · INFO · CUSTOMER CARE ]
            </div>
          </div>
          <h1 className="display-h1 text-5xl sm:text-7xl text-zinc-50">
            Help &amp; Contact
          </h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Order questions, sizing help, returns, wholesale — send a message
            and we&apos;ll respond within 48 hours. Scroll for shipping,
            returns, sizing and care info.
          </p>
        </div>

        {/* ABOUT */}
        <InfoBlock id="about" icon={Sparkles} title="About PDOB" testid="info-about">
          <p>
            PDOB / STREET WEAR is an independent apparel label founded in 2020.
            Every piece is designed in-house and released as a limited drop —
            no restocks, no wholesale, no shortcuts.
          </p>
          <p className="text-zinc-500">
            All designs are original. Heavyweight fabrics, honest prints,
            attention to detail. Made for people who wear their fits every day.
          </p>
        </InfoBlock>

        {/* SHIPPING */}
        <InfoBlock id="shipping" icon={Truck} title="Shipping" testid="info-shipping">
          <ul className="list-none space-y-2">
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// US</span> — Free standard shipping on orders over
              <span className="text-zinc-100"> $150</span>. Under $150 flat rate $9.99. Delivery
              3–5 business days.
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// INTL</span> — Calculated at checkout. Delivery 7–14
              business days. Duties &amp; taxes not included.
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// PROCESSING</span> — Orders ship within 1–2 business
              days. You&apos;ll receive a tracking number by email once your
              order is on the way.
            </li>
          </ul>
        </InfoBlock>

        {/* RETURNS */}
        <InfoBlock id="returns" icon={RefreshCw} title="Returns" testid="info-returns">
          <ul className="list-none space-y-2">
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// WINDOW</span> — 30 days from delivery for a full
              refund or exchange.
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// CONDITION</span> — Unworn, unwashed, with original
              tags attached. Halloween Capsule items and sale items are final
              sale (exchange only).
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// HOW</span> — Message the form below with your
              order number and we&apos;ll email you a return label.
            </li>
          </ul>
        </InfoBlock>

        {/* SIZE GUIDE */}
        <InfoBlock id="size-guide" icon={Ruler} title="Size Guide" testid="info-size-guide">
          <p className="text-zinc-400 mb-3">
            All fits are slightly relaxed. If between sizes, size down for a
            fitted look, size up for oversized.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-left font-mono-tech text-xs">
              <thead>
                <tr className="border-b-2 border-zinc-700 text-zinc-200 uppercase tracking-widest">
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2 pr-4">Chest (in)</th>
                  <th className="py-2 pr-4">Length (in)</th>
                  <th className="py-2 pr-4">Sleeve (in)</th>
                </tr>
              </thead>
              <tbody className="text-zinc-300">
                {[
                  ["S", "36-38", "27", "24"],
                  ["M", "38-40", "28", "24.5"],
                  ["L", "42-44", "29", "25"],
                  ["XL", "46-48", "30", "25.5"],
                  ["XXL", "50-52", "31", "26"],
                  ["3XL", "54-56", "32", "26.5"],
                ].map((row) => (
                  <tr key={row[0]} className="border-b border-zinc-800">
                    {row.map((v, i) => (
                      <td key={i} className={`py-2 pr-4 ${i === 0 ? "text-zinc-100" : ""}`}>
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </InfoBlock>

        {/* CARE */}
        <InfoBlock id="care" icon={Droplets} title="Care Instructions" testid="info-care">
          <ul className="list-none space-y-2">
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// WASH</span> — Machine wash cold, inside out, with
              like colors.
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// DRY</span> — Tumble dry low or hang dry. Do not
              iron directly on prints or rhinestones.
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// BLEACH</span> — Do not bleach. Do not dry clean.
            </li>
            <li>
              <span className="font-mono-tech text-xs uppercase text-zinc-200">// PRINTS</span> — All graphic prints are heat-cured
              for maximum durability, but avoid high-heat ironing on the print
              itself.
            </li>
          </ul>
        </InfoBlock>

        {/* CONTACT FORM */}
        <div id="form" className="pt-6 scroll-mt-24">
          <div className="font-mono-tech text-[10px] tracking-[0.3em] text-zinc-200 mb-2 uppercase">
            [ SEND US A MESSAGE ]
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-zinc-50 uppercase tracking-tight leading-none">
            Get in Touch
          </h2>
        </div>

        <form
          onSubmit={submit}
          className="border-2 border-zinc-800 bg-zinc-950 p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4"
          data-testid="contact-form"
        >
          <div>
            <label className="font-mono-tech text-xs uppercase text-zinc-400 mb-1 block">Name *</label>
            <input value={form.name} onChange={handle("name")} required data-testid="contact-name" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100" />
          </div>
          <div>
            <label className="font-mono-tech text-xs uppercase text-zinc-400 mb-1 block">Email *</label>
            <input type="email" value={form.email} onChange={handle("email")} required data-testid="contact-email" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100" />
          </div>
          <div>
            <label className="font-mono-tech text-xs uppercase text-zinc-400 mb-1 block">Inquiry Type</label>
            <select value={form.inquiry_type} onChange={handle("inquiry_type")} data-testid="contact-type" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100 uppercase">
              <option value="general">General</option>
              <option value="order">Order / Shipping</option>
              <option value="return">Returns / Exchanges</option>
              <option value="wholesale">Wholesale / Press</option>
            </select>
          </div>
          <div>
            <label className="font-mono-tech text-xs uppercase text-zinc-400 mb-1 block">Subject</label>
            <input value={form.subject} onChange={handle("subject")} data-testid="contact-subject" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100" />
          </div>
          <div className="md:col-span-2">
            <label className="font-mono-tech text-xs uppercase text-zinc-400 mb-1 block">Message *</label>
            <textarea value={form.message} onChange={handle("message")} rows={6} required data-testid="contact-message" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-zinc-300 focus:outline-none px-3 py-2 font-mono-tech text-sm text-zinc-100" />
          </div>
          <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="inline-flex items-center gap-2 bg-zinc-300 hover:bg-zinc-200 disabled:opacity-60 text-zinc-950 px-6 py-3 font-display text-lg tracking-widest uppercase brutal-shadow-orange hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all"
            >
              <Send className="w-4 h-4" /> {loading ? "Sending..." : "Send Message"}
            </button>
            {sent && <span className="font-mono-tech text-xs uppercase text-lime-500" data-testid="contact-success">// Message received. We&apos;ll be in touch.</span>}
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
};

export default ContactPage;
